import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { toast } from 'react-toastify';
import { errorHandler } from '@/utils/functions/errorHandler';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { generateParams } from '@/utils/functions/generateParams';
import { env } from '@/utils/constants/env';

export interface MasterdataColumn {
  name: string;
  type: string;
  is_required: boolean;
  is_unique: boolean;
  is_searchable: boolean;
  options: any;
  slug: string;
}

export interface MasterdataRecord {
  [key: string]: any;
}

export interface MasterdataTable {
  [key: string]: any;
  name: string;
  description?: string;
  columns: MasterdataColumn[];
  records?: MasterdataRecord[];
  slug?: string;
  recordDetail?: MasterdataRecord;
}

interface MasterdataState {
  masterdataList: MasterdataTable[];
  masterdataDetail: MasterdataTable | null;
  masterdataRecords: MasterdataRecord[];
  recordDetail: MasterdataRecord | null;
  totalElements: number;
  totalPages: number;
  isLoading: boolean;
  error: unknown;

  fetchMasterdataList: (paramsDataConfig: DataConfig ,  tenantId: number) => Promise<void>;
  fetchMasterdataRecords: (slug: string,tenantId: number, page?: number, size?: number ,) => Promise<void>;
  }

const useMasterdataState = create<MasterdataState>((set, get) => ({
  masterdataList: [],
  masterdataDetail: null,
  masterdataRecords: [],
  recordDetail: null,
  totalElements: 0,
  totalPages: 1,
  isLoading: false,
  error: null,

  fetchMasterdataList: async (paramsDataConfig, tenantId) => {
    set({ isLoading: true, error: null });
    try {
      
      if (!tenantId) {
        toast.error('Tenant not found');
        set({ isLoading: false });
        return;
      }

      const params = generateParams(paramsDataConfig);
      const url = new URL(env.apiTasklistBaseUrl + `/api/v1/probis/masterdata/${tenantId}${params}`);
      const { data } = await axiosInstance.get(url.toString());
      const list = Array.isArray(data?.data?.content) ? data.data.content : data.data || [];

      set({
        masterdataList: list,
        totalElements: data?.data?.totalElements ?? list.length,
        totalPages: data?.data?.totalPages ?? 1,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching masterdata list:', error);
      errorHandler(error);
      set({ error, masterdataList: [], isLoading: false });
    }
  },

  fetchMasterdataRecords: async (slug, tenantId, page = 0, size = 10) => {
    set({ isLoading: true, error: null });
    try {
      if (!tenantId) {
        toast.error('Tenant not found');
        set({ isLoading: false });
        return;
      }

      const url = new URL(
        `${env.apiTasklistBaseUrl}/api/v1/probis/masterdata/${tenantId}/${slug}/records?paginated=true&page=${page}&size=${size}`,
      );

      const { data } = await axiosInstance.get(url.toString());

      const records = Array.isArray(data?.data?.content) ? data.data.content : data.data || [];

      set({
        masterdataRecords: records,
        totalElements: data?.data?.totalElements ?? records.length,
        totalPages: data?.data?.totalPages ?? 1,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching masterdata records:', error);
      errorHandler(error);
      set({ error, masterdataRecords: [], isLoading: false });
    }
  },

}));

export default useMasterdataState;
